import { supabase } from './supabase';

/**
 * The app's only route to Gemini.
 *
 * Why this exists: the app used to call generativelanguage.googleapis.com
 * directly with `process.env.REACT_APP_GEMINI_API_KEY` in the query string. A
 * CRA env var is substituted into the JavaScript at build time, so that key was
 * compiled into main.*.js and shipped inside the iOS binary, the Android binary
 * and the web bundle, where anyone could read it. The key is on a postpay
 * Google billing account, so reading it meant being able to bill the owner.
 *
 * Every Gemini call now goes through the `gemini-proxy` edge function, which
 * holds the key in project secrets. Nothing here knows it.
 *
 * This deliberately returns the raw `Response` rather than parsed JSON, and the
 * function mirrors Gemini's status and body. That is what lets the call sites
 * keep the `if (!res.ok)` and `await res.json()` handling they already had,
 * unchanged, so moving the key did not also mean rewriting the parsing.
 */
const FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/gemini-proxy`;

export async function geminiGenerate({ model, contents, generationConfig, tools, systemInstruction }) {
  // The proxy requires a real signed-in user, because an anonymous caller
  // spending the owner's Gemini budget is the thing this whole change exists to
  // prevent. Answering locally with a 401 rather than sending a request the
  // function will refuse keeps the failure readable in the console.
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    return new Response(JSON.stringify({ error: 'not_signed_in' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  return fetch(FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // The user's token, not the anon key. The function resolves the caller
      // with admin.auth.getUser(jwt); handing it the anon key identifies
      // nobody and is refused.
      Authorization: `Bearer ${token}`,
      apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ model, contents, generationConfig, tools, systemInstruction }),
  });
}
